import * as React from 'react';
import { Cpu, AlertCircle, AlertTriangle, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';

export type PLCQuality = 'good' | 'uncertain' | 'bad' | 'stale';

export interface PLCStatusProps {
  /** Quality from the comms layer. */
  quality: PLCQuality;
  /** Optional driver/protocol label (e.g. "OPC-UA"). */
  driver?: string;
  /** Optional last-poll latency in ms. */
  latencyMs?: number;
  /** Compact mode shows just the icon and quality. */
  compact?: boolean;
  className?: string;
}

const META: Record<PLCQuality, {
  label: string;
  color: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}> = {
  good: {
    label: 'Good',
    color: 'hsl(var(--state-running))',
    Icon: Cpu,
  },
  uncertain: {
    label: 'Uncertain',
    color: 'hsl(var(--alarm-minor))',
    Icon: AlertTriangle,
  },
  bad: {
    label: 'Bad',
    color: 'hsl(var(--alarm-critical))',
    Icon: AlertCircle,
  },
  stale: {
    label: 'Stale',
    color: 'hsl(var(--muted-foreground))',
    Icon: Clock,
  },
};

/**
 * Tyndo PLCStatus — comms-quality pill (ISA-101 grammar).
 *
 * Use on any UI that surfaces PLC/OPC-UA/Modbus device data so operators can
 * tell at a glance whether the value they're seeing is trustworthy.
 */
export function PLCStatus({
  quality,
  driver,
  latencyMs,
  compact,
  className,
}: PLCStatusProps) {
  const meta = META[quality];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium',
        compact && 'px-1.5',
        className
      )}
      style={{
        backgroundColor: `${meta.color.replace('hsl(', 'hsl(').slice(0, -1)} / 0.12)`,
        color: meta.color,
        boxShadow: `inset 0 0 0 1px ${meta.color.slice(0, -1)} / 0.3)`,
      }}
      title={`${driver ?? 'Device'} comms: ${meta.label}${
        latencyMs !== undefined ? ` · ${latencyMs}ms` : ''
      }`}
    >
      <meta.Icon className="h-3 w-3" />
      {!compact && (
        <span className="t-mono uppercase tracking-wider">{meta.label}</span>
      )}
      {driver && !compact && (
        <span className="text-muted-foreground">· {driver}</span>
      )}
      {latencyMs !== undefined && !compact && (
        <span className="t-tabular tabular-nums text-muted-foreground">
          {latencyMs}ms
        </span>
      )}
    </span>
  );
}
